import { z } from 'zod';
import {
  ESIGIBILITA_IVA_CODES,
  MODALITA_PAGAMENTO_CODES,
  REGIME_FISCALE_CODES,
  TIPO_CASSA_CODES,
  TIPO_DOCUMENTO_CODES,
  TIPO_RITENUTA_CODES,
} from './reference-tables';

/**
 * Tracciato FatturaPA in snake_case così come lo accetta/restituisce l'API
 * gov-it di A-Cube (`Invoice.InvoiceInput-write` / `Invoice-read.payload`).
 * Tutti gli importi e le percentuali viaggiano come STRINGHE decimali.
 */

export const FatturaPaDecimalSchema = z.string().regex(/^-?\d+\.\d{2,8}$/, 'importo decimale con almeno 2 cifre');
export const FatturaPaAliquotaSchema = z.string().regex(/^\d+\.\d+$/, 'aliquota decimale');
export const FatturaPaDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const FatturaPaIdFiscaleSchema = z.object({
  id_paese: z.string().length(2),
  id_codice: z.string().min(1).max(28),
});

export const FatturaPaAnagraficaSchema = z.object({
  denominazione: z.string().min(1).max(80).optional(),
  nome: z.string().min(1).max(60).optional(),
  cognome: z.string().min(1).max(60).optional(),
  titolo: z.string().min(2).max(10).optional(),
  cod_eori: z.string().min(13).max(17).optional(),
});

export const FatturaPaSedeSchema = z.object({
  indirizzo: z.string().min(1).max(60),
  numero_civico: z.string().min(1).max(8).optional(),
  cap: z.string().regex(/^\d{5}$/),
  comune: z.string().min(1).max(60),
  provincia: z.string().length(2).optional(),
  nazione: z.string().length(2),
});

export const FatturaPaDatiTrasmissioneSchema = z.object({
  codice_destinatario: z.string().regex(/^[A-Za-z0-9]{6,7}$/),
  pec_destinatario: z.string().min(7).max(256).optional(),
});

export const FatturaPaCedentePrestatoreSchema = z.object({
  dati_anagrafici: z.object({
    id_fiscale_iva: FatturaPaIdFiscaleSchema,
    codice_fiscale: z.string().min(11).max(16).optional(),
    anagrafica: FatturaPaAnagraficaSchema,
    regime_fiscale: z.enum(REGIME_FISCALE_CODES),
  }),
  sede: FatturaPaSedeSchema,
  iscrizione_rea: z.object({
    ufficio: z.string().length(2),
    numero_rea: z.string().min(1).max(20),
    capitale_sociale: FatturaPaDecimalSchema.optional(),
    socio_unico: z.enum(['SU', 'SM']).optional(),
    stato_liquidazione: z.enum(['LS', 'LN']),
  }).optional(),
  contatti: z.object({
    telefono: z.string().min(5).max(12).optional(),
    fax: z.string().min(5).max(12).optional(),
    email: z.string().min(7).max(256).optional(),
  }).optional(),
  riferimento_amministrazione: z.string().min(1).max(20).optional(),
});

export const FatturaPaCessionarioCommittenteSchema = z.object({
  dati_anagrafici: z.object({
    id_fiscale_iva: FatturaPaIdFiscaleSchema.optional(),
    codice_fiscale: z.string().min(11).max(16).optional(),
    anagrafica: FatturaPaAnagraficaSchema,
  }),
  sede: FatturaPaSedeSchema,
});

export const FatturaPaHeaderSchema = z.object({
  dati_trasmissione: FatturaPaDatiTrasmissioneSchema,
  cedente_prestatore: FatturaPaCedentePrestatoreSchema,
  cessionario_committente: FatturaPaCessionarioCommittenteSchema,
  soggetto_emittente: z.enum(['CC', 'TZ']).optional(),
});

export const FatturaPaScontoMaggiorazioneSchema = z.object({
  tipo: z.enum(['SC', 'MG']),
  percentuale: FatturaPaAliquotaSchema.optional(),
  importo: FatturaPaDecimalSchema.optional(),
});

export const FatturaPaDatiRitenutaSchema = z.object({
  tipo_ritenuta: z.enum(TIPO_RITENUTA_CODES),
  importo_ritenuta: FatturaPaDecimalSchema,
  aliquota_ritenuta: FatturaPaAliquotaSchema,
  causale_pagamento: z.string().min(1).max(2),
});

export const FatturaPaDatiBolloSchema = z.object({
  bollo_virtuale: z.literal('SI'),
  importo_bollo: FatturaPaDecimalSchema.optional(),
});

export const FatturaPaDatiCassaPrevidenzialeSchema = z.object({
  tipo_cassa: z.enum(TIPO_CASSA_CODES),
  al_cassa: FatturaPaAliquotaSchema,
  importo_contributo_cassa: FatturaPaDecimalSchema,
  imponibile_cassa: FatturaPaDecimalSchema.optional(),
  aliquota_iva: FatturaPaAliquotaSchema,
  ritenuta: z.literal('SI').optional(),
  natura: z.string().min(2).max(4).optional(),
  riferimento_amministrazione: z.string().min(1).max(20).optional(),
});

export const FatturaPaDatiGeneraliDocumentoSchema = z.object({
  tipo_documento: z.enum(TIPO_DOCUMENTO_CODES),
  divisa: z.string().length(3),
  data: FatturaPaDateSchema,
  numero: z.string().min(1).max(20),
  dati_ritenuta: z.array(FatturaPaDatiRitenutaSchema).optional(),
  dati_bollo: FatturaPaDatiBolloSchema.optional(),
  dati_cassa_previdenziale: z.array(FatturaPaDatiCassaPrevidenzialeSchema).optional(),
  sconto_maggiorazione: z.array(FatturaPaScontoMaggiorazioneSchema).optional(),
  importo_totale_documento: FatturaPaDecimalSchema.optional(),
  arrotondamento: FatturaPaDecimalSchema.optional(),
  causale: z.array(z.string().min(1).max(200)).optional(),
  art73: z.literal('SI').optional(),
});

export const FatturaPaDocumentoCorrelatoSchema = z.object({
  riferimento_numero_linea: z.array(z.number().int().positive()).optional(),
  id_documento: z.string().min(1).max(20),
  data: FatturaPaDateSchema.optional(),
  num_item: z.string().min(1).max(20).optional(),
  codice_commessa_convenzione: z.string().min(1).max(100).optional(),
  codice_cup: z.string().min(1).max(15).optional(),
  codice_cig: z.string().min(1).max(15).optional(),
});

export const FatturaPaDatiDdtSchema = z.object({
  numero_ddt: z.string().min(1).max(20),
  data_ddt: FatturaPaDateSchema,
  riferimento_numero_linea: z.array(z.number().int().positive()).optional(),
});

export const FatturaPaDatiGeneraliSchema = z.object({
  dati_generali_documento: FatturaPaDatiGeneraliDocumentoSchema,
  dati_ordine_acquisto: z.array(FatturaPaDocumentoCorrelatoSchema).optional(),
  dati_contratto: z.array(FatturaPaDocumentoCorrelatoSchema).optional(),
  dati_convenzione: z.array(FatturaPaDocumentoCorrelatoSchema).optional(),
  dati_ricezione: z.array(FatturaPaDocumentoCorrelatoSchema).optional(),
  dati_fatture_collegate: z.array(FatturaPaDocumentoCorrelatoSchema).optional(),
  dati_ddt: z.array(FatturaPaDatiDdtSchema).optional(),
});

export const FatturaPaDettaglioLineaSchema = z.object({
  numero_linea: z.number().int().positive(),
  tipo_cessione_prestazione: z.enum(['SC', 'PR', 'AB', 'AC']).optional(),
  descrizione: z.string().min(1).max(1000),
  quantita: FatturaPaDecimalSchema.optional(),
  unita_misura: z.string().min(1).max(10).optional(),
  prezzo_unitario: FatturaPaDecimalSchema,
  sconto_maggiorazione: z.array(FatturaPaScontoMaggiorazioneSchema).optional(),
  prezzo_totale: FatturaPaDecimalSchema,
  aliquota_iva: FatturaPaAliquotaSchema,
  ritenuta: z.literal('SI').optional(),
  natura: z.string().min(2).max(4).optional(),
  riferimento_amministrazione: z.string().min(1).max(20).optional(),
});

export const FatturaPaDatiRiepilogoSchema = z.object({
  aliquota_iva: FatturaPaAliquotaSchema,
  natura: z.string().min(2).max(4).optional(),
  spese_accessorie: FatturaPaDecimalSchema.optional(),
  arrotondamento: FatturaPaDecimalSchema.optional(),
  imponibile_importo: FatturaPaDecimalSchema,
  imposta: FatturaPaDecimalSchema,
  esigibilita_iva: z.enum(ESIGIBILITA_IVA_CODES).optional(),
  riferimento_normativo: z.string().min(1).max(100).optional(),
});

export const FatturaPaDatiBeniServiziSchema = z.object({
  dettaglio_linee: z.array(FatturaPaDettaglioLineaSchema).min(1),
  dati_riepilogo: z.array(FatturaPaDatiRiepilogoSchema).min(1),
});

export const FatturaPaDettaglioPagamentoSchema = z.object({
  beneficiario: z.string().min(1).max(200).optional(),
  modalita_pagamento: z.enum(MODALITA_PAGAMENTO_CODES),
  data_riferimento_termini_pagamento: FatturaPaDateSchema.optional(),
  giorni_termini_pagamento: z.number().int().nonnegative().optional(),
  data_scadenza_pagamento: FatturaPaDateSchema.optional(),
  importo_pagamento: FatturaPaDecimalSchema,
  istituto_finanziario: z.string().min(1).max(80).optional(),
  iban: z.string().min(15).max(34).optional(),
  abi: z.string().length(5).optional(),
  cab: z.string().length(5).optional(),
  bic: z.string().min(8).max(11).optional(),
});

export const FatturaPaDatiPagamentoSchema = z.object({
  condizioni_pagamento: z.enum(['TP01', 'TP02', 'TP03']),
  dettaglio_pagamento: z.array(FatturaPaDettaglioPagamentoSchema).min(1),
});

export const FatturaPaAllegatoSchema = z.object({
  nome_attachment: z.string().min(1).max(60),
  algoritmo_compressione: z.string().min(1).max(10).optional(),
  formato_attachment: z.string().min(1).max(10).optional(),
  descrizione_attachment: z.string().min(1).max(100).optional(),
  attachment: z.string().min(1),
});

export const FatturaPaBodySchema = z.object({
  dati_generali: FatturaPaDatiGeneraliSchema,
  dati_beni_servizi: FatturaPaDatiBeniServiziSchema,
  dati_pagamento: z.array(FatturaPaDatiPagamentoSchema).optional(),
  allegati: z.array(FatturaPaAllegatoSchema).optional(),
});

export const FatturaPaInvoiceInputSchema = z.object({
  fattura_elettronica_header: FatturaPaHeaderSchema,
  fattura_elettronica_body: z.array(FatturaPaBodySchema).min(1),
});

export type FatturaPaIdFiscale = z.infer<typeof FatturaPaIdFiscaleSchema>;
export type FatturaPaAnagrafica = z.infer<typeof FatturaPaAnagraficaSchema>;
export type FatturaPaSede = z.infer<typeof FatturaPaSedeSchema>;
export type FatturaPaDatiTrasmissione = z.infer<typeof FatturaPaDatiTrasmissioneSchema>;
export type FatturaPaCedentePrestatore = z.infer<typeof FatturaPaCedentePrestatoreSchema>;
export type FatturaPaCessionarioCommittente = z.infer<typeof FatturaPaCessionarioCommittenteSchema>;
export type FatturaPaHeader = z.infer<typeof FatturaPaHeaderSchema>;
export type FatturaPaScontoMaggiorazione = z.infer<typeof FatturaPaScontoMaggiorazioneSchema>;
export type FatturaPaDatiRitenuta = z.infer<typeof FatturaPaDatiRitenutaSchema>;
export type FatturaPaDatiBollo = z.infer<typeof FatturaPaDatiBolloSchema>;
export type FatturaPaDatiCassaPrevidenziale = z.infer<typeof FatturaPaDatiCassaPrevidenzialeSchema>;
export type FatturaPaDatiGeneraliDocumento = z.infer<typeof FatturaPaDatiGeneraliDocumentoSchema>;
export type FatturaPaDocumentoCorrelato = z.infer<typeof FatturaPaDocumentoCorrelatoSchema>;
export type FatturaPaDatiDdt = z.infer<typeof FatturaPaDatiDdtSchema>;
export type FatturaPaDatiGenerali = z.infer<typeof FatturaPaDatiGeneraliSchema>;
export type FatturaPaDettaglioLinea = z.infer<typeof FatturaPaDettaglioLineaSchema>;
export type FatturaPaDatiRiepilogo = z.infer<typeof FatturaPaDatiRiepilogoSchema>;
export type FatturaPaDatiBeniServizi = z.infer<typeof FatturaPaDatiBeniServiziSchema>;
export type FatturaPaDettaglioPagamento = z.infer<typeof FatturaPaDettaglioPagamentoSchema>;
export type FatturaPaDatiPagamento = z.infer<typeof FatturaPaDatiPagamentoSchema>;
export type FatturaPaAllegato = z.infer<typeof FatturaPaAllegatoSchema>;
export type FatturaPaBody = z.infer<typeof FatturaPaBodySchema>;
export type FatturaPaInvoiceInput = z.infer<typeof FatturaPaInvoiceInputSchema>;

// ───────────────────────── risorse di lettura gov-it ─────────────────────────

/** Stato di lavorazione A-Cube (`Invoice-read.marking`), autoritativo per l'invio SdI. */
export const ACUBE_INVOICE_MARKINGS = ['waiting', 'sent', 'quarantena', 'invoice-error', 'rejected', 'archived'] as const;
export const AcubeInvoiceMarkingSchema = z.enum(ACUBE_INVOICE_MARKINGS);
export type AcubeInvoiceMarking = z.infer<typeof AcubeInvoiceMarkingSchema>;

/** Tipi di notifica SdI (2 lettere) restituiti da `GET /invoices/{uuid}/notifications`. */
export const ACUBE_SDI_NOTIFICATION_TYPES = ['RC', 'NS', 'MC', 'NE', 'DT', 'AT', 'MT', 'EC', 'SE'] as const;
export const AcubeSdiNotificationTypeSchema = z.enum(ACUBE_SDI_NOTIFICATION_TYPES);
export type AcubeSdiNotificationType = z.infer<typeof AcubeSdiNotificationTypeSchema>;

export const AcubeInvoiceNotificationSchema = z.object({
  uuid: z.string(),
  invoice_uuid: z.string().nullable().optional(),
  created_at: z.string(),
  type: z.string(),
  file_name: z.string().nullable().optional(),
  message: z.record(z.unknown()).nullable().optional(),
  downloaded: z.boolean().optional(),
  downloaded_at: z.string().nullable().optional(),
});
export type AcubeInvoiceNotification = z.infer<typeof AcubeInvoiceNotificationSchema>;

export const AcubePreservedDocumentStatusSchema = z.enum(['to_be_stored', 'sent', 'stored', 'error']);
export type AcubePreservedDocumentStatus = z.infer<typeof AcubePreservedDocumentStatusSchema>;

export const AcubePreservedDocumentSchema = z.object({
  uuid: z.string(),
  created_at: z.string(),
  status: AcubePreservedDocumentStatusSchema,
  receipt_received_at: z.string().nullable().optional(),
  object_type: z.string().nullable().optional(),
  object_id: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
  preserved_object_id: z.string().nullable().optional(),
  weight: z.number().nullable().optional(),
});
export type AcubePreservedDocument = z.infer<typeof AcubePreservedDocumentSchema>;

export const AcubePreservedDocumentReceiptSchema = z.object({ xml: z.string() });
export type AcubePreservedDocumentReceipt = z.infer<typeof AcubePreservedDocumentReceiptSchema>;

/** `Invoice-read`: `payload` è il tracciato (JSON serializzato o XML) e `notifications` sono IRI. */
export const AcubeInvoiceReadSchema = z.object({
  uuid: z.string(),
  created_at: z.string(),
  type: z.number().int().nullable().optional(),
  payload: z.string().nullable().optional(),
  sender: z.string().nullable().optional(),
  recipient: z.string().nullable().optional(),
  notifications: z.array(z.string()).optional(),
  sdi_file_name: z.string().nullable().optional(),
  sdi_file_id: z.string().nullable().optional(),
  signed: z.boolean().optional(),
  legally_stored: z.boolean().optional(),
  preserved_document: AcubePreservedDocumentSchema.nullable().optional(),
  downloaded: z.boolean().optional(),
  downloaded_at: z.string().nullable().optional(),
  marking: z.string().nullable().optional(),
  notice: z.string().nullable().optional(),
  retry_information: z.array(z.string()).optional(),
  transmission_format: z.string().nullable().optional(),
  document_type: z.string().nullable().optional(),
  to_pa: z.boolean().optional(),
});
export type AcubeInvoiceRead = z.infer<typeof AcubeInvoiceReadSchema>;

export const AcubeInvoiceCreatedSchema = z.object({ uuid: z.string().min(1) });
export type AcubeInvoiceCreated = z.infer<typeof AcubeInvoiceCreatedSchema>;

/** Payload del webhook `supplier-invoice` (fattura passiva ricevuta dallo SdI). */
export const AcubeSupplierInvoiceWebhookSchema = z.object({
  invoice: z.object({
    uuid: z.string().min(1),
    filename: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
    payload: z.unknown().optional(),
  }),
});
export type AcubeSupplierInvoiceWebhook = z.infer<typeof AcubeSupplierInvoiceWebhookSchema>;

/** Body dell'ingest manuale (diagnostica master) di una fattura passiva per uuid. */
export const AcubeSupplierInvoiceIngestBodySchema = z.object({
  uuid: z.string().uuid(),
}).strict();
export type AcubeSupplierInvoiceIngestBody = z.infer<typeof AcubeSupplierInvoiceIngestBodySchema>;
